import { ArrowRight } from 'lucide-react';
import { getTimeLeft } from '../utils/getTimeLeft';
import { propFirmData } from '../data/propFirmDataHomepage';
import { glassCTAData } from '../data/glassCTAData';
import { propFirmLogos } from '../data/propFirmLogos';
import { PromoCTAButton } from './PromoCTAButton';

interface GlassCTAProps {
  firmId: string;
  firm?: any;
  discount?: any;
  variant?: 'primary' | 'secondary' | 'compact';
  position?: 'top' | 'bottom';
  promoUsedElsewhere?: boolean;
}

export function GlassCTA({
  firmId,
  firm,
  discount,
  variant = 'primary',
  position = 'top',
  promoUsedElsewhere = false
}: GlassCTAProps) {
  if (!firmId) return null;

  const fallbackFirm = propFirmData[firmId];
  const displayFirm = firm || fallbackFirm;
  const displayDiscount = discount || fallbackFirm?.evaluation?.discount || {};

  const { code: promoCode, validUntil } = displayDiscount || {};
  const timeLeft = validUntil ? getTimeLeft(validUntil) : null;
  const adData =
    glassCTAData[firmId] ||
    glassCTAData.noMatch ||
    glassCTAData.default;

  const logo = propFirmLogos[firmId]?.logo;

  const headline =
    variant === 'promo'
      ? adData.promoHeadline
      : position === 'bottom'
      ? adData.bottomHeadline
      : adData.topHeadline;

  const description =
    variant === 'promo'
      ? adData.promoDescription
      : position === 'bottom'
      ? adData.bottomDescription
      : adData.topDescription;

  return (
    <div className="max-w-3xl w-full bg-white/5 backdrop-blur-lg p-6 sm:p-8 rounded-xl shadow-lg flex items-center gap-6 text-left hover:shadow-yellow-400/10 hover:ring-yellow-300/10 transition-all mx-auto">
      {logo && (
        <div className="flex-shrink-0">
          <img
            src={logo}
            alt={firmId}
            className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
            loading="lazy"
          />
        </div>
      )}

      <div className="flex flex-col items-start">
        <h3 className="text-xl sm:text-2xl font-extrabold text-yellow-100 tracking-tight mb-2">
          {headline}
        </h3>

        <p className="text-yellow-400 text-base sm:text-lg mb-4">
          {description}
        </p>

        <div className="text-yellow-300 font-semibold mb-4">
          {promoCode ? (
            <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-300 px-3 py-1 rounded-full text-sm">
              Use code: <span className="bg-yellow-300 text-gray-900 px-2 py-0.5 rounded font-bold">{promoCode}</span>
            </div>
          ) : (
            <>{adData.defaultText}</>
          )}
        </div>

        {timeLeft && (
          <div className="text-sm text-yellow-400 mb-4">
            {timeLeft} left!
          </div>
        )}

        <PromoCTAButton
          firmId={firmId}
          discount={displayDiscount}
          variant="homepage"
          className="flex items-center gap-2 justify-center hover:bg-yellow-400/20 transition"
        >
          {promoCode ? 'Get Funded Now' : adData.defaultText}
          <ArrowRight className="w-4 h-4" />
        </PromoCTAButton>
      </div>
    </div>
  );
}

export default GlassCTA;
